// @flow

import { createContext, useContext } from 'react';
import type { RegistrationOptions } from './services/webauthn';

export type EnrollmentState =
  | 'BANK_LINKING'
  | 'INITIATING'
  | 'BANK_REDIRECT'
  | 'POLLING'
  | 'READY_TO_AUTH'
  | 'AUTHENTICATING'
  | 'SUBMITTING_AUTH'
  | 'SUCCESS'
  | 'ERROR';

export type EnrollmentData = {|
  +state: EnrollmentState,
  +contract: string,
  +cpf: string,
  +bankId: string,
  +bankName: string,
  +bankLogoUrl: string,
  +registrationOptions: ?RegistrationOptions,
  +errorMessage: string,
  +isAuthError: boolean,
  +isTerminalError: boolean,
  +intentUrl: string,
|};

export type EnrollmentAction =
  | {| +type: 'SET_CPF', +cpf: string |}
  | {| +type: 'SET_BANK', +bankId: string, +bankName: string, +bankLogoUrl: string |}
  | {| +type: 'START_INITIATION' |}
  | {| +type: 'INITIATION_SUCCESS' |}
  | {| +type: 'START_POLLING' |}
  | {| +type: 'REGISTRATION_OPTIONS_RECEIVED', +registrationOptions: RegistrationOptions, +intentUrl: string |}
  | {| +type: 'START_AUTHENTICATION' |}
  | {| +type: 'START_SUBMITTING_AUTH' |}
  | {| +type: 'ENROLLMENT_SUCCESS', +intentUrl: string |}
  | {| +type: 'SET_ERROR', +errorMessage: string |}
  | {| +type: 'SET_TERMINAL_ERROR', +errorMessage: string, +intentUrl: string |}
  | {| +type: 'SET_AUTH_ERROR', +errorMessage: string |}
  | {| +type: 'RETRY' |}
  | {| +type: 'RETRY_AUTH' |};

type EnrollmentContextType = {|
  +data: EnrollmentData,
  +dispatch: (action: EnrollmentAction) => void,
|};

const EnrollmentContext = createContext<?EnrollmentContextType>(null);

export const EnrollmentProvider = EnrollmentContext.Provider;

export function useEnrollment(): EnrollmentContextType {
  const context = useContext(EnrollmentContext);
  if (!context) {
    throw new Error('EnrollmentProvider not found');
  }
  return context;
}
